import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import HistoryIcon from '@mui/icons-material/History'
import { inventorySyncAdminService } from '../services'
import { formatDateTime } from '../utils/dateFormat'
import { formatOrganizationLabel } from '../utils/organizationLabel'
import { useOrganizationInfiniteList } from '../hooks/useOrganizationInfiniteList'
import UploadLogStatusChip from '../components/UploadLogStatusChip'
import UploadErrorDetailsDialog from '../components/UploadErrorDetailsDialog'
import { useUploadHistoryPolling } from '../hooks/useUploadHistoryPolling'
import {
  buildInventorySyncSubmitItem,
  isFractionalQuantity,
  validateInventorySyncQuantity,
} from '../utils/inventorySyncLooseItem'

const emptyNewProduct = () => ({
  source: '',
  product_name: '',
  article_code: '',
  barcode: '',
  quantity: 1,
  unit_cost: '',
  unit_price: '',
  expiry_date: '',
  is_loose_item: false,
})

const SHWAPNO_IDENTIFIER_PATTERN = /^\d{7}$/

const isPositivePriceInput = (value) => {
  if (value === '' || value == null) return false
  const n = Number(value)
  return Number.isFinite(n) && n > 0
}

const isSellingPriceNotExceedingMrp = (unitCost, unitPrice) => {
  const cost = Number(unitCost)
  const price = Number(unitPrice)
  if (!Number.isFinite(cost) || !Number.isFinite(price)) return true
  return cost <= price
}

const csvEscape = (value) => {
  const s = value == null ? '' : String(value)
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`
  return s
}

const downloadInvalidRowsCsv = (rows, filename = 'inventory-sync-errors.csv') => {
  if (!rows?.length) return
  const headers = [
    'row_number',
    'identifier',
    'source',
    'product_name',
    'error_code',
    'error_message',
    'blocking_sync_id',
    'blocking_org_id',
    'blocking_sync_status',
  ]
  const lines = [
    headers.join(','),
    ...rows.map((err) => {
      const rowData = err.row_data || {}
      return [
        err.row_number ?? '',
        rowData.identifier || rowData.sku || '',
        rowData.source || '',
        rowData.product_name || '',
        err.error_code || '',
        err.error_message || '',
        rowData.blocking_sync_id || '',
        rowData.blocking_org_id || '',
        rowData.blocking_sync_status || '',
      ]
        .map(csvEscape)
        .join(',')
    }),
  ]
  const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

const InvalidSyncRowsTable = ({ rows, onRejectBlockingSync }) => {
  const [rejecting, setRejecting] = useState({})
  const [rejected, setRejected] = useState({})

  if (!rows?.length) return null

  const handleRejectBlocking = async (syncId) => {
    setRejecting((s) => ({ ...s, [syncId]: true }))
    try {
      await onRejectBlockingSync(syncId)
      setRejected((s) => ({ ...s, [syncId]: true }))
    } catch {
      // ignore — user can navigate to the sync manually
    } finally {
      setRejecting((s) => ({ ...s, [syncId]: false }))
    }
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">
          {rows.length} invalid row{rows.length !== 1 ? 's' : ''}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={() =>
            downloadInvalidRowsCsv(
              rows,
              `inventory-sync-errors-${new Date().toISOString().split('T')[0]}.csv`,
            )
          }
        >
          Export errors (CSV)
        </Button>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Row</TableCell>
              <TableCell>Identifier</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Blocking sync</TableCell>
              <TableCell>Error</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((err) => {
              const blockingSyncId = err.row_data?.blocking_sync_id
              const blockingOrgId = err.row_data?.blocking_org_id
              const blockingStatus = err.row_data?.blocking_sync_status
              const isFreeable =
                onRejectBlockingSync &&
                blockingSyncId &&
                blockingStatus === 'submitted' &&
                !rejected[blockingSyncId]
              return (
                <TableRow key={`${err.row_number}-${err.error_message}`}>
                  <TableCell>{err.row_number ?? '—'}</TableCell>
                  <TableCell>
                    {err.row_data?.identifier || err.row_data?.sku || '—'}
                  </TableCell>
                  <TableCell>{err.row_data?.source || '—'}</TableCell>
                  <TableCell>
                    {blockingSyncId ? (
                      <Box>
                        <Link
                          href={`/inventory-sync/${blockingSyncId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Sync #{blockingSyncId}
                        </Link>
                        {blockingOrgId ? (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Shop #{blockingOrgId}
                            {blockingStatus
                              ? ` · ${rejected[blockingSyncId] ? 'rejected ✓' : blockingStatus}`
                              : ''}
                          </Typography>
                        ) : null}
                        {isFreeable && (
                          <Button
                            size="small"
                            color="warning"
                            variant="outlined"
                            disabled={!!rejecting[blockingSyncId]}
                            onClick={() => handleRejectBlocking(blockingSyncId)}
                            sx={{ mt: 0.5, fontSize: '0.7rem', py: 0, px: 1 }}
                          >
                            {rejecting[blockingSyncId] ? 'Rejecting…' : 'Reject & free'}
                          </Button>
                        )}
                        {rejected[blockingSyncId] && (
                          <Typography variant="caption" color="success.main" display="block">
                            Freed — re-upload to proceed
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{err.error_message}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

const PartialFailureDialog = ({ open, syncId, successful, failed, total, errors, onAccept, onReject }) => {
  const [showErrors, setShowErrors] = useState(false)
  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Partial Import — Action Required
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Chip label={`${total} rows total`} size="small" />
          <Chip label={`${successful} will be imported`} color="success" size="small" />
          <Chip label={`${failed} failed`} color="error" size="small" />
        </Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>{failed} row{failed !== 1 ? 's' : ''} could not be imported</strong> (e.g. duplicate
          barcodes, invalid prices, or unknown identifiers). The remaining <strong>{successful} item{successful !== 1 ? 's' : ''}</strong> are
          ready to proceed.
          <br />
          Choose whether to <strong>accept the passing rows</strong> and continue, or{' '}
          <strong>reject the entire upload</strong> and fix the errors first.
        </Alert>
        <Box sx={{ mb: 1 }}>
          <Button
            size="small"
            variant="text"
            onClick={() => setShowErrors((v) => !v)}
          >
            {showErrors ? 'Hide' : 'Show'} {failed} failing row{failed !== 1 ? 's' : ''}
          </Button>
          {errors.length > 0 && (
            <Button
              size="small"
              variant="text"
              onClick={() =>
                downloadInvalidRowsCsv(
                  errors,
                  `partial-failure-errors-sync-${syncId}-${new Date().toISOString().split('T')[0]}.csv`,
                )
              }
            >
              Export errors (CSV)
            </Button>
          )}
        </Box>
        {showErrors && errors.length > 0 && (
          <TableContainer sx={{ maxHeight: 320, mb: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Row</TableCell>
                  <TableCell>Identifier</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Blocking sync</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errors.map((err) => {
                  const blockingSyncId = err.row_data?.blocking_sync_id
                  const blockingOrgId = err.row_data?.blocking_org_id
                  const blockingStatus = err.row_data?.blocking_sync_status
                  return (
                    <TableRow key={`${err.row_number}-${err.error_message}`}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{err.row_number ?? '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {err.row_data?.identifier || err.row_data?.sku || '—'}
                      </TableCell>
                      <TableCell>{err.row_data?.product_name || '—'}</TableCell>
                      <TableCell>
                        {blockingSyncId ? (
                          <Box>
                            <Link
                              href={`/inventory-sync/${blockingSyncId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Sync #{blockingSyncId}
                            </Link>
                            {blockingOrgId && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                Shop #{blockingOrgId}{blockingStatus ? ` · ${blockingStatus}` : ''}
                              </Typography>
                            )}
                          </Box>
                        ) : '—'}
                      </TableCell>
                      <TableCell sx={{ color: 'error.main' }}>{err.error_message}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button variant="outlined" color="error" onClick={onReject}>
          Reject entire upload
        </Button>
        <Button variant="contained" color="primary" onClick={onAccept}>
          Accept {successful} passing row{successful !== 1 ? 's' : ''} →
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const InventorySyncCreatePage = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState(0)
  const [selectedOrg, setSelectedOrg] = useState(null)
  const [orgInputValue, setOrgInputValue] = useState('')
  const [syncName, setSyncName] = useState('')
  const [notes, setNotes] = useState('')
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(false)

  const [csvFile, setCsvFile] = useState(null)
  const [isDragOver, setIsDragOver] = useState(false)

  const [sessionSyncId, setSessionSyncId] = useState(null)
  const [lineItems, setLineItems] = useState([])
  const [productSearch, setProductSearch] = useState('')
  const [productOptions, setProductOptions] = useState([])
  const [productsLoading, setProductsLoading] = useState(false)
  const [newProductForm, setNewProductForm] = useState(emptyNewProduct())
  const [showNewProductForm, setShowNewProductForm] = useState(false)

  const [uploadLogs, setUploadLogs] = useState([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [logsPage, setLogsPage] = useState(0)
  const [logsRowsPerPage, setLogsRowsPerPage] = useState(10)
  const [logsTotalCount, setLogsTotalCount] = useState(0)
  const [invalidRows, setInvalidRows] = useState([])
  const [errorDetailsDialog, setErrorDetailsDialog] = useState({
    open: false,
    uploadLog: null,
  })
  const [partialFailureDialog, setPartialFailureDialog] = useState({
    open: false,
    syncId: null,
    successful: 0,
    failed: 0,
    total: 0,
    errors: [],
  })
  const logsFetchInFlightRef = useRef(false)

  const {
    organizations: orgOptionsBase,
    loading: orgsLoading,
    loadingMore: orgsLoadingMore,
    setSearchText: setOrgSearchText,
    resetSearch: resetOrgSearch,
    handleListboxScroll,
  } = useOrganizationInfiniteList({ pageSize: 20 })

  const organizationOptions = useMemo(() => {
    if (!selectedOrg) return orgOptionsBase
    if (orgOptionsBase.some((o) => o.id === selectedOrg.id)) {
      return orgOptionsBase
    }
    return [selectedOrg, ...orgOptionsBase]
  }, [orgOptionsBase, selectedOrg])

  const orgId = selectedOrg?.id

  const isNewProductPricingValid = useMemo(
    () =>
      isPositivePriceInput(newProductForm.unit_cost) &&
      isPositivePriceInput(newProductForm.unit_price) &&
      isSellingPriceNotExceedingMrp(newProductForm.unit_cost, newProductForm.unit_price),
    [newProductForm.unit_cost, newProductForm.unit_price],
  )

  const isSessionPricingValid = useMemo(
    () =>
      lineItems.length > 0 &&
      lineItems.every(
        (row) =>
          isPositivePriceInput(row.unit_cost) &&
          isPositivePriceInput(row.unit_price) &&
          isSellingPriceNotExceedingMrp(row.unit_cost, row.unit_price),
      ),
    [lineItems],
  )

  const validateBasics = () => {
    if (!orgId) {
      setMessage({ type: 'error', text: 'Select a shop first.' })
      return false
    }
    if (!syncName.trim() || syncName.trim().length < 3) {
      setMessage({
        type: 'error',
        text: 'Sync name is required (at least 3 characters).',
      })
      return false
    }
    return true
  }

  const handleDownloadTemplate = async () => {
    try {
      const response = await inventorySyncAdminService.downloadCreationCsvTemplate()
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'inventory-sync-template.csv'
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to download template',
      })
    }
  }

  const handleCsvUpload = async () => {
    if (!validateBasics() || !csvFile) {
      if (!csvFile) {
        setMessage({ type: 'error', text: 'Choose a CSV file to upload.' })
      }
      return
    }
    setLoading(true)
    setMessage(null)
    setInvalidRows([])
    try {
      const result = await inventorySyncAdminService.importCreationCsv(orgId, csvFile, {
        name: syncName.trim(),
        notes: notes.trim() || undefined,
      })
      const rowErrors = result.errors || []
      setCsvFile(null)
      if (result.sync_id && result.failed > 0 && result.successful > 0) {
        // Partial failure — let the admin decide
        setPartialFailureDialog({
          open: true,
          syncId: result.sync_id,
          successful: result.successful,
          failed: result.failed,
          total: result.processed,
          errors: rowErrors,
        })
        setInvalidRows(rowErrors)
        return
      }
      if (rowErrors.length > 0) {
        setInvalidRows(rowErrors)
      }
      setMessage({
        type: result.failed > 0 ? 'warning' : 'success',
        text: `Sync #${result.sync_id} submitted with ${result.successful} item(s)${
          result.failed > 0 ? `; ${result.failed} row(s) failed` : ''
        }.`,
        errors: rowErrors,
      })
      if (result.sync_id && result.failed === 0) {
        navigate(`/inventory-sync/${result.sync_id}`)
      }
    } catch (err) {
      const data = err.response?.data
      const rowErrors = data?.errors || []
      setInvalidRows(rowErrors)
      setMessage({
        type: 'error',
        text: data?.message || 'CSV import failed',
        errors: rowErrors,
        logId: data?.logId,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleStartSession = async () => {
    if (!validateBasics()) return
    setLoading(true)
    setMessage(null)
    try {
      const check = await inventorySyncAdminService.checkName(orgId, syncName.trim())
      if (!check.available) {
        setMessage({ type: 'error', text: 'Sync name already exists for this shop.' })
        return
      }
      const created = await inventorySyncAdminService.createForOrg(orgId, {
        name: syncName.trim(),
        notes: notes.trim() || undefined,
      })
      setSessionSyncId(created.id)
      setLineItems([])
      setMessage({
        type: 'success',
        text: `Session started (sync #${created.id}). Add items and submit when ready.`,
      })
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to start session',
      })
    } finally {
      setLoading(false)
    }
  }

  const searchProducts = useCallback(async (term) => {
    if (!term?.trim()) {
      setProductOptions([])
      return
    }
    setProductsLoading(true)
    try {
      const data = await inventorySyncAdminService.getProducts({
        search: term.trim(),
        page: 1,
        limit: 25,
      })
      setProductOptions(data.items || [])
    } catch (err) {
      console.error('Catalog product search failed', err)
      setProductOptions([])
      setMessage({
        type: 'error',
        text: err.response?.data?.message || 'Failed to search catalog products',
      })
    } finally {
      setProductsLoading(false)
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => searchProducts(productSearch), 350)
    return () => clearTimeout(timer)
  }, [productSearch, searchProducts])

  const addCatalogProduct = (product) => {
    if (!product?.id) return
    const hasCost =
      product.unit_cost != null && product.unit_cost !== '' && !Number.isNaN(Number(product.unit_cost))
    const hasPrice =
      product.unit_price != null &&
      product.unit_price !== '' &&
      !Number.isNaN(Number(product.unit_price))
    setLineItems((prev) => [
      ...prev,
      {
        key: `catalog-${product.id}-${Date.now()}`,
        admin_product_id: product.id,
        product_name: product.product_name,
        article_code: product.article_code || product.sku,
        quantity: 1,
        unit_cost: hasCost ? Number(product.unit_cost) : '',
        unit_price: hasPrice
          ? Number(product.unit_price)
          : product.mrp != null && product.mrp !== ''
            ? Number(product.mrp)
            : '',
        expiry_date: '',
        is_non_shwapno: product.source === 'external',
        catalog_source: product.source || null,
        needs_pricing: !hasCost || !hasPrice,
      },
    ])
    setProductSearch('')
    setProductOptions([])
    if (!hasCost || !hasPrice) {
      setMessage({
        type: 'info',
        text: `${product.product_name || 'Product'} has no warehouse price on file. Enter Cost and MRP on the line before submitting.`,
      })
    }
  }

  const addNewProductLine = () => {
    const form = newProductForm
    if (!form.product_name.trim()) {
      setMessage({ type: 'error', text: 'Product name is required for new products.' })
      return
    }
    if (!form.article_code.trim() && form.source !== 'external') {
      setMessage({ type: 'error', text: 'Identifier is required.' })
      return
    }
    if (
      form.source === 'shwapno' &&
      form.article_code.trim() &&
      !SHWAPNO_IDENTIFIER_PATTERN.test(form.article_code.trim())
    ) {
      setMessage({
        type: 'error',
        text: 'Shwapno source requires a 7-digit identifier.',
      })
      return
    }
    const qtyResult = validateInventorySyncQuantity(form.quantity, {
      isLooseItem: !!form.is_loose_item,
    })
    if (!qtyResult.ok) {
      setMessage({ type: 'error', text: qtyResult.error })
      return
    }
    if (!isSellingPriceNotExceedingMrp(form.unit_cost, form.unit_price)) {
      setMessage({
        type: 'error',
        text: 'Selling Price must not exceed MRP.',
      })
      return
    }
    const isExternal = form.source === 'external'
    const trimmedIdentifier = form.article_code.trim()
    const isLooseItem = qtyResult.isLooseItem
    const newProduct = {
      product_name: form.product_name.trim(),
      barcodes: form.barcode.trim() ? [form.barcode.trim()] : undefined,
      ...(isLooseItem ? { is_loose_item: true } : {}),
    }
    if (isExternal) {
      newProduct.source = 'external'
      if (trimmedIdentifier) {
        newProduct.article_code = trimmedIdentifier
      }
    } else if (form.source === 'shwapno') {
      newProduct.source = 'shwapno'
      newProduct.article_code = trimmedIdentifier
    } else if (trimmedIdentifier) {
      newProduct.article_code = trimmedIdentifier
    }
    setLineItems((prev) => [
      ...prev,
      {
        key: `new-${Date.now()}`,
        new_product: newProduct,
        product_name: form.product_name.trim(),
        article_code: trimmedIdentifier,
        source: form.source,
        quantity: qtyResult.value,
        unit_cost: Number(form.unit_cost || 0),
        unit_price: Number(form.unit_price || 0),
        expiry_date: form.expiry_date || undefined,
        is_non_shwapno: isExternal,
        is_loose_item: isLooseItem,
      },
    ])
    setNewProductForm(emptyNewProduct())
    setShowNewProductForm(false)
    setMessage(null)
  }

  const updateLineItem = (key, field, value) => {
    setLineItems((prev) =>
      prev.map((row) => {
        if (row.key !== key) return row
        if (
          field === 'is_loose_item' &&
          !value &&
          isFractionalQuantity(row.quantity)
        ) {
          // Fractional qty implies Loose Item — cannot clear while qty stays fractional.
          return row
        }
        const next = { ...row, [field]: value }
        if (field === 'is_loose_item' && row.new_product) {
          next.new_product = {
            ...row.new_product,
            ...(value ? { is_loose_item: true } : {}),
          }
          if (!value) {
            delete next.new_product.is_loose_item
          }
        }
        if (field === 'quantity' && row.new_product) {
          const fractional = isFractionalQuantity(value)
          if (fractional || next.is_loose_item) {
            next.is_loose_item = true
            next.new_product = { ...next.new_product, is_loose_item: true }
          }
        }
        return next
      }),
    )
  }

  const removeLineItem = (key) => {
    setLineItems((prev) => prev.filter((row) => row.key !== key))
  }

  const handleSubmitSession = async () => {
    if (!sessionSyncId) {
      setMessage({ type: 'error', text: 'Start a session first.' })
      return
    }
    if (lineItems.length === 0) {
      setMessage({ type: 'error', text: 'Add at least one line item.' })
      return
    }
    const missingPrice = lineItems.find((row) => {
      const cost = Number(row.unit_cost)
      const price = Number(row.unit_price)
      return (
        row.unit_cost === '' ||
        row.unit_price === '' ||
        Number.isNaN(cost) ||
        Number.isNaN(price) ||
        cost < 0 ||
        price < 0
      )
    })
    if (missingPrice) {
      setMessage({
        type: 'error',
        text: `Enter Cost and MRP for "${missingPrice.product_name || missingPrice.article_code || 'line item'}" before submitting.`,
      })
      return
    }
    const costExceedsMrp = lineItems.find(
      (row) => !isSellingPriceNotExceedingMrp(row.unit_cost, row.unit_price),
    )
    if (costExceedsMrp) {
      setMessage({
        type: 'error',
        text: `Selling Price must not exceed MRP for "${costExceedsMrp.product_name || costExceedsMrp.article_code || 'line item'}".`,
      })
      return
    }
    const invalidQty = lineItems.find((row) => {
      const result = validateInventorySyncQuantity(row.quantity, {
        isLooseItem: !!(row.is_loose_item || row.new_product?.is_loose_item),
      })
      return !result.ok
    })
    if (invalidQty) {
      const result = validateInventorySyncQuantity(invalidQty.quantity, {
        isLooseItem: !!(
          invalidQty.is_loose_item || invalidQty.new_product?.is_loose_item
        ),
      })
      setMessage({
        type: 'error',
        text: `${result.error} (${invalidQty.product_name || invalidQty.article_code || 'line item'})`,
      })
      return
    }
    setLoading(true)
    setMessage(null)
    setInvalidRows([])
    try {
      const items = lineItems.map((row) => buildInventorySyncSubmitItem(row))
      const result = await inventorySyncAdminService.submit(sessionSyncId, {
        items,
        notes: notes.trim() || undefined,
      })
      setMessage({
        type: 'success',
        text: `Sync #${result.id} submitted with ${result.saved_items} item(s).`,
      })
      navigate(`/inventory-sync/${result.id}`)
    } catch (err) {
      const data = err.response?.data
      const rowErrors = data?.errors || []
      setInvalidRows(rowErrors)
      setMessage({
        type: 'error',
        text: data?.message || 'Submit failed',
        errors: rowErrors,
      })
    } finally {
      setLoading(false)
    }
  }

  const handlePartialAccept = () => {
    const { syncId } = partialFailureDialog
    setPartialFailureDialog((d) => ({ ...d, open: false }))
    setMessage({
      type: 'warning',
      text: `Sync #${syncId} submitted with ${partialFailureDialog.successful} item(s); ${partialFailureDialog.failed} row(s) were skipped.`,
      errors: partialFailureDialog.errors,
    })
    navigate(`/inventory-sync/${syncId}`)
  }

  const handlePartialReject = async () => {
    const { syncId, failed, errors: failErrors } = partialFailureDialog
    setPartialFailureDialog((d) => ({ ...d, open: false }))

    // Reject the submitted sync so it doesn't surface in approvals
    try {
      await inventorySyncAdminService.reject(syncId, 'Rejected by uploader due to partial import failure')
    } catch {
      // non-critical — sync stays as submitted but at least the admin is aware
    }

    // Also reject any other blocking syncs referenced in IDENTIFIER_PENDING errors
    // so their identifiers are freed for the next upload attempt.
    const otherBlockingSyncIds = [
      ...new Set(
        (failErrors || [])
          .filter((e) => e.error_code === 'IDENTIFIER_PENDING' && e.row_data?.blocking_sync_id)
          .map((e) => e.row_data.blocking_sync_id)
          .filter((id) => id !== syncId),
      ),
    ]
    const autoRejected = []
    for (const blockId of otherBlockingSyncIds) {
      try {
        await inventorySyncAdminService.reject(blockId, 'Auto-rejected: blocking sync freed by uploader')
        autoRejected.push(blockId)
      } catch {
        // May already be rejected or approved — ignore
      }
    }

    // Classify errors: IDENTIFIER_PENDING ones referencing now-freed syncs are resolved
    const freedSyncIds = new Set([syncId, ...autoRejected])
    const resolvedErrors = (failErrors || []).filter(
      (e) =>
        e.error_code === 'IDENTIFIER_PENDING' &&
        freedSyncIds.has(e.row_data?.blocking_sync_id),
    )
    const remainingErrors = (failErrors || []).filter(
      (e) => !resolvedErrors.includes(e),
    )

    setInvalidRows(remainingErrors.length > 0 ? remainingErrors : failErrors)

    const resolvedNote =
      resolvedErrors.length > 0
        ? ` ${resolvedErrors.length} identifier conflict(s) are now resolved.`
        : ''
    const stillFailedNote =
      remainingErrors.length > 0
        ? ` ${remainingErrors.length} other error(s) still need fixing.`
        : ''
    setMessage({
      type: remainingErrors.length > 0 ? 'warning' : 'info',
      text: `Upload rejected — Sync #${syncId} has been rejected.${resolvedNote}${stillFailedNote} Re-upload to proceed.`,
      errors: remainingErrors.length > 0 ? remainingErrors : undefined,
    })
  }

  const fetchUploadLogs = useCallback(async ({ silent = false } = {}) => {
    if (logsFetchInFlightRef.current) return
    logsFetchInFlightRef.current = true
    try {
      if (!silent) setLogsLoading(true)
      const response = await inventorySyncAdminService.getUploadLogs({
        page: logsPage + 1,
        limit: logsRowsPerPage,
      })
      const items = (response.items || []).filter((log) => {
        if (!orgId) return true
        return String(log.organization_id) === String(orgId)
      })
      setUploadLogs(items)
      setLogsTotalCount(response.pagination?.total || items.length)
    } catch {
      if (!silent) {
        setUploadLogs([])
        setLogsTotalCount(0)
      }
    } finally {
      if (!silent) setLogsLoading(false)
      logsFetchInFlightRef.current = false
    }
  }, [logsPage, logsRowsPerPage, orgId])

  useEffect(() => {
    if (activeTab === 2) fetchUploadLogs()
  }, [activeTab, fetchUploadLogs])

  useUploadHistoryPolling(activeTab === 2, fetchUploadLogs)

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Create Inventory Sync</Typography>
        <Button variant="outlined" onClick={() => navigate('/inventory-sync')}>
          View approvals
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add shop inventory by identifier without inventory lot IDs. Submitted syncs follow the
        same approval workflow as mobile store syncs.
      </Typography>

      {message && (
        <Alert
          severity={message.type}
          sx={{ mb: 2 }}
          onClose={() => {
            setMessage(null)
            setInvalidRows([])
          }}
        >
          {message.text}
          {message.errors?.length > 0 && message.errors.length <= 5 && (
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              {message.errors.map((err) => (
                <li key={`${err.row_number}-${err.error_message}`}>
                  Row {err.row_number}: {err.error_message}
                </li>
              ))}
            </Box>
          )}
          {message.errors?.length > 5 && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              See invalid rows table below for all {message.errors.length} errors.
            </Typography>
          )}
        </Alert>
      )}

      <InvalidSyncRowsTable
        rows={invalidRows}
        onRejectBlockingSync={async (blockingSyncId) => {
          await inventorySyncAdminService.reject(
            blockingSyncId,
            'Rejected via error table: freed identifier for re-upload',
          )
        }}
      />

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Autocomplete
            options={organizationOptions}
            loading={orgsLoading || orgsLoadingMore}
            value={selectedOrg}
            inputValue={orgInputValue}
            onInputChange={(_, value, reason) => {
              setOrgInputValue(value)
              if (reason === 'input') setOrgSearchText(value)
            }}
            onChange={(_, value) => {
              setSelectedOrg(value)
              setSessionSyncId(null)
              setLineItems([])
              if (value) {
                setOrgInputValue(formatOrganizationLabel(value))
                resetOrgSearch()
              } else {
                setOrgInputValue('')
              }
            }}
            getOptionLabel={(option) => formatOrganizationLabel(option)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            ListboxProps={{ onScroll: handleListboxScroll }}
            renderInput={(params) => (
              <TextField {...params} label="Shop Id" required />
            )}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Sync name"
              value={syncName}
              onChange={(e) => setSyncName(e.target.value)}
              required
              sx={{ minWidth: 240, flex: 1 }}
            />
            <TextField
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ minWidth: 240, flex: 1 }}
            />
          </Box>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab label="CSV Upload" icon={<CloudUploadIcon />} iconPosition="start" />
        <Tab label="UI Session" icon={<AddIcon />} iconPosition="start" />
        <Tab label="Upload History" icon={<HistoryIcon />} iconPosition="start" />
      </Tabs>

      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Required columns: Identifier, Quantity, Selling Price, MRP. Identifier is optional
              for external products (server assigns 9xxxxxx when blank). Optional: Product Name,
              Source (blank/auto, shwapno, or external), Barcode, Expiry, Non-Shwapno (yes/no;
              external new products default to yes). Explicit Shwapno requires a 7-digit identifier.
              Blank source tries catalog SKU match, then barcode lookup. Fractional Quantity values
              (e.g. 0.5, 1.25) are automatically classified as Loose Items — no extra CSV column is
              required. Whole-number quantities remain regular items. Quantities support up to 2
              decimal places.
            </Typography>
            <Button
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              sx={{ mb: 2 }}
            >
              Download template
            </Button>
            <Box
              onDragOver={(e) => {
                e.preventDefault()
                setIsDragOver(true)
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(e) => {
                e.preventDefault()
                setIsDragOver(false)
                const file = e.dataTransfer.files?.[0]
                if (file) setCsvFile(file)
              }}
              sx={{
                border: '2px dashed',
                borderColor: isDragOver ? 'primary.main' : 'divider',
                borderRadius: 1,
                p: 3,
                textAlign: 'center',
                mb: 2,
              }}
            >
              <input
                type="file"
                accept=".csv,text/csv"
                hidden
                id="inventory-sync-csv-input"
                onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
              />
              <label htmlFor="inventory-sync-csv-input">
                <Button variant="outlined" component="span">
                  Choose CSV file
                </Button>
              </label>
              {csvFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {csvFile.name}
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
              disabled={loading}
              onClick={handleCsvUpload}
            >
              Upload and submit sync
            </Button>
          </CardContent>
        </Card>
      )}

      {activeTab === 1 && (
        <Card>
          <CardContent>
            {!sessionSyncId ? (
              <Button
                variant="contained"
                disabled={loading}
                onClick={handleStartSession}
              >
                Start sync session
              </Button>
            ) : (
              <>
                <Chip label={`Session #${sessionSyncId}`} color="primary" sx={{ mb: 2 }} />
                <Autocomplete
                  options={productOptions}
                  loading={productsLoading}
                  inputValue={productSearch}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input' || reason === 'clear') {
                      setProductSearch(value)
                    }
                  }}
                  onChange={(_, value) => value && addCatalogProduct(value)}
                  filterOptions={(options) => options}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  getOptionLabel={(option) =>
                    typeof option === 'string'
                      ? option
                      : `${option.product_name || ''} (${option.article_code || option.sku || ''})`
                  }
                  renderOption={(props, option) => {
                    const { key, ...rest } = props
                    const sku = option.article_code || option.sku || '—'
                    const priceLabel =
                      option.unit_price != null
                        ? `MRP ${option.unit_price}`
                        : 'No warehouse price — enter on line'
                    return (
                      <li key={key} {...rest}>
                        <Box sx={{ py: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {option.product_name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            Identifier: {sku} · {priceLabel}
                          </Typography>
                        </Box>
                      </li>
                    )
                  }}
                  noOptionsText={
                    productsLoading
                      ? 'Searching…'
                      : productSearch.trim()
                        ? 'No catalog products found'
                        : 'Type a product name or article code'
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search catalog product"
                      placeholder="Name or article code"
                      helperText="Searches all published admin catalog products"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {productsLoading ? (
                              <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setShowNewProductForm((v) => !v)}
                  sx={{ mb: 2 }}
                >
                  {showNewProductForm ? 'Hide new product form' : 'Add new product'}
                </Button>
                {showNewProductForm && (
                  <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', mb: 2 }}>
                    <TextField
                      label="Product name"
                      value={newProductForm.product_name}
                      onChange={(e) =>
                        setNewProductForm((f) => ({ ...f, product_name: e.target.value }))
                      }
                    />
                    <TextField
                      label="Identifier"
                      value={newProductForm.article_code}
                      onChange={(e) =>
                        setNewProductForm((f) => ({ ...f, article_code: e.target.value }))
                      }
                      helperText={
                        newProductForm.source === 'external'
                          ? 'Optional. Leave blank to auto-assign (9xxxxxx).'
                          : newProductForm.source === 'shwapno'
                            ? 'Required. Must be exactly 7 digits.'
                            : 'Required. 7-digit SKU or barcode for auto-match.'
                      }
                      placeholder={
                        newProductForm.source === 'external' ? 'Optional custom code' : undefined
                      }
                    />
                    <FormControl>
                      <InputLabel>Source</InputLabel>
                      <Select
                        label="Source"
                        displayEmpty
                        value={newProductForm.source}
                        renderValue={(value) => {
                          if (value === 'shwapno') return 'Shwapno'
                          if (value === 'external') return 'External'
                          return 'Auto (SKU or barcode)'
                        }}
                        onChange={(e) =>
                          setNewProductForm((f) => ({
                            ...f,
                            source: e.target.value,
                          }))
                        }
                      >
                        <MenuItem value="">Auto (SKU or barcode)</MenuItem>
                        <MenuItem value="shwapno">Shwapno</MenuItem>
                        <MenuItem value="external">External</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label="Barcode"
                      value={newProductForm.barcode}
                      onChange={(e) =>
                        setNewProductForm((f) => ({ ...f, barcode: e.target.value }))
                      }
                    />
                    <TextField
                      label="Quantity"
                      type="number"
                      inputProps={{
                        min: newProductForm.is_loose_item ? 0.01 : 1,
                        step: newProductForm.is_loose_item ? 0.01 : 1,
                      }}
                      value={newProductForm.quantity}
                      onChange={(e) => {
                        const quantity = e.target.value
                        setNewProductForm((f) => ({
                          ...f,
                          quantity,
                          is_loose_item:
                            f.is_loose_item || isFractionalQuantity(quantity),
                        }))
                      }}
                      helperText={
                        newProductForm.is_loose_item
                          ? 'Loose Item: decimals allowed (up to 2 places)'
                          : 'Whole numbers; enable Loose Item for decimals'
                      }
                    />
                    <FormControlLabel
                      sx={{ alignItems: 'flex-start', mt: 0.5 }}
                      control={
                        <Checkbox
                          checked={!!newProductForm.is_loose_item}
                          onChange={(e) =>
                            setNewProductForm((f) => ({
                              ...f,
                              is_loose_item: e.target.checked,
                            }))
                          }
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">Loose Item</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Maps the product to the Loose Commodity division so it can be sold by
                            custom weight at POS.
                          </Typography>
                        </Box>
                      }
                    />
                    <TextField
                      label="Selling Price"
                      type="number"
                      value={newProductForm.unit_cost}
                      onChange={(e) =>
                        setNewProductForm((f) => ({ ...f, unit_cost: e.target.value }))
                      }
                    />
                    <TextField
                      label="MRP"
                      type="number"
                      value={newProductForm.unit_price}
                      onChange={(e) =>
                        setNewProductForm((f) => ({ ...f, unit_price: e.target.value }))
                      }
                    />
                    {isPositivePriceInput(newProductForm.unit_cost) &&
                      isPositivePriceInput(newProductForm.unit_price) &&
                      !isSellingPriceNotExceedingMrp(
                        newProductForm.unit_cost,
                        newProductForm.unit_price,
                      ) && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ gridColumn: '1 / -1', mt: -1 }}
                        >
                          Selling Price must not exceed MRP.
                        </Typography>
                      )}
                    <TextField
                      label="Expiry"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      value={newProductForm.expiry_date}
                      onChange={(e) =>
                        setNewProductForm((f) => ({ ...f, expiry_date: e.target.value }))
                      }
                    />
                    <Button
                      variant="contained"
                      disabled={!isNewProductPricingValid}
                      onClick={addNewProductLine}
                    >
                      Add line
                    </Button>
                  </Box>
                )}
                <Divider sx={{ my: 2 }} />
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell>Identifier</TableCell>
                        <TableCell>Qty</TableCell>
                        <TableCell>Cost</TableCell>
                        <TableCell>MRP</TableCell>
                        <TableCell>Expiry</TableCell>
                        <TableCell>Loose Item</TableCell>
                        <TableCell>Non-Shwapno</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lineItems.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            No items yet
                          </TableCell>
                        </TableRow>
                      ) : (
                        lineItems.map((row) => (
                          <TableRow key={row.key}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>{row.product_name}</span>
                                {(row.is_loose_item || row.new_product?.is_loose_item) && (
                                  <Chip size="small" label="Loose Item" color="warning" variant="outlined" />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {row.article_code ||
                                (row.new_product?.source === 'external' && !row.article_code
                                  ? 'Auto (9xxxxxx)'
                                  : '—')}
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                inputProps={{
                                  min:
                                    row.is_loose_item || row.new_product?.is_loose_item
                                      ? 0.01
                                      : 1,
                                  step:
                                    row.is_loose_item || row.new_product?.is_loose_item
                                      ? 0.01
                                      : 1,
                                }}
                                value={row.quantity}
                                onChange={(e) =>
                                  updateLineItem(row.key, 'quantity', e.target.value)
                                }
                                sx={{ width: 88 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                value={row.unit_cost}
                                placeholder="Cost"
                                error={
                                  (row.needs_pricing && (row.unit_cost === '' || row.unit_cost == null)) ||
                                  !isSellingPriceNotExceedingMrp(row.unit_cost, row.unit_price)
                                }
                                helperText={
                                  !isSellingPriceNotExceedingMrp(row.unit_cost, row.unit_price)
                                    ? 'Exceeds MRP'
                                    : undefined
                                }
                                onChange={(e) =>
                                  updateLineItem(row.key, 'unit_cost', e.target.value)
                                }
                                sx={{ width: 88 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                value={row.unit_price}
                                placeholder="MRP"
                                error={row.needs_pricing && (row.unit_price === '' || row.unit_price == null)}
                                onChange={(e) =>
                                  updateLineItem(row.key, 'unit_price', e.target.value)
                                }
                                sx={{ width: 88 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={row.expiry_date || ''}
                                onChange={(e) =>
                                  updateLineItem(row.key, 'expiry_date', e.target.value)
                                }
                              />
                            </TableCell>
                            <TableCell>
                              {row.new_product ? (
                                <Checkbox
                                  size="small"
                                  checked={!!(row.is_loose_item || row.new_product?.is_loose_item)}
                                  onChange={(e) =>
                                    updateLineItem(row.key, 'is_loose_item', e.target.checked)
                                  }
                                />
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Checkbox
                                size="small"
                                checked={!!row.is_non_shwapno}
                                onChange={(e) =>
                                  updateLineItem(row.key, 'is_non_shwapno', e.target.checked)
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" onClick={() => removeLineItem(row.key)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    disabled={loading || !isSessionPricingValid}
                    onClick={handleSubmitSession}
                  >
                    Submit sync
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 2 && (
        <Card>
          <CardContent>
            {logsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>File</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Rows</TableCell>
                        <TableCell>Success</TableCell>
                        <TableCell>Failed</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {uploadLogs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            No upload history{orgId ? ' for this shop' : ''}
                          </TableCell>
                        </TableRow>
                      ) : (
                        uploadLogs.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell>{log.file_name}</TableCell>
                            <TableCell>
                              <UploadLogStatusChip status={log.status} />
                            </TableCell>
                            <TableCell>{log.total_rows}</TableCell>
                            <TableCell>{log.successful_imports}</TableCell>
                            <TableCell>{log.failed_imports}</TableCell>
                            <TableCell>
                              {formatDateTime(log.created_at)}
                            </TableCell>
                            <TableCell>
                              {log.failed_imports > 0 && (
                                <Button
                                  size="small"
                                  onClick={() =>
                                    setErrorDetailsDialog({ open: true, uploadLog: log })
                                  }
                                >
                                  Errors
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={logsTotalCount}
                  page={logsPage}
                  onPageChange={(_, p) => setLogsPage(p)}
                  rowsPerPage={logsRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setLogsRowsPerPage(parseInt(e.target.value, 10))
                    setLogsPage(0)
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}

      <UploadErrorDetailsDialog
        open={errorDetailsDialog.open}
        uploadLog={errorDetailsDialog.uploadLog}
        onClose={() => setErrorDetailsDialog({ open: false, uploadLog: null })}
      />

      <PartialFailureDialog
        open={partialFailureDialog.open}
        syncId={partialFailureDialog.syncId}
        successful={partialFailureDialog.successful}
        failed={partialFailureDialog.failed}
        total={partialFailureDialog.total}
        errors={partialFailureDialog.errors}
        onAccept={handlePartialAccept}
        onReject={handlePartialReject}
      />
    </Container>
  )
}

export default InventorySyncCreatePage
